import { memo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "wouter";
import { getRegionPathFromName } from "@shared/routeSlugs";
import { getCategoryPathFromName } from "@shared/seo";
import { MobileBrowseSheet } from "@/components/home/MobileBrowseSheet";

const MOBILE_INTRO =
  "Find events across Albany and the surrounding Capital District in one place, then jump to the original source for the latest details.";
const MOBILE_FILTERS_HINT = "Use Filters for all regions, sources, and advanced options.";
const discoverySectionHeadingClassName =
  "text-[11px] font-black uppercase tracking-[0.16em] text-muted-foreground md:text-[11px] md:tracking-[0.16em]";
const discoverySectionCardClassName =
  "rounded-[18px] border border-[#e6dfd2] bg-[linear-gradient(180deg,rgba(250,251,248,0.96),rgba(255,255,255,0.98))] px-3 py-3 md:px-3 md:py-3 lg:px-4 lg:py-3";
const discoveryChipClassName =
  "inline-flex min-h-[44px] min-w-0 max-w-full items-center justify-center rounded-[14px] border px-3 py-2 text-center text-[13px] font-semibold whitespace-normal break-words transition-[background-color,border-color,box-shadow,transform] duration-100 hover:bg-[#f0f6f1] hover:border-[#bfd4c4] active:translate-y-[1px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2 md:min-h-[34px] md:max-w-none md:shrink-0 md:px-3 md:py-1.5 md:text-[13px] md:whitespace-nowrap lg:min-h-[32px] lg:px-3 lg:py-1 lg:text-[13px]";
const discoveryChipIdleStyle = {
  border: "1.5px solid #d7e4d9",
  backgroundColor: "#f8fbf7",
  color: "#335547",
} as const;
const discoveryChipActiveStyle = {
  border: "1.5px solid #9fc9ab",
  backgroundColor: "#e7f3e8",
  color: "#23483b",
  boxShadow: "var(--brutal-shadow-sm)",
} as const;

interface HomeLandingIntroProps {
  heading: string;
  intro: string;
  regionPills: readonly { name: string; active: boolean }[];
  categoryPills: readonly { name: string; active: boolean }[];
  onRegionClick: (region: string) => void;
  showAllRegions?: boolean;
  onAllRegionsClick?: () => void;
  onCategoryClick: (category: string) => void;
  mobileResultCount: number | null;
  onMobileRegionSelect: (region: string) => void;
  onMobileCategorySelect: (category: string) => void;
  collapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
}

function HomeLandingIntroInner({
  heading,
  intro,
  regionPills,
  categoryPills,
  onRegionClick,
  showAllRegions = false,
  onAllRegionsClick,
  onCategoryClick,
  mobileResultCount,
  onMobileRegionSelect,
  onMobileCategorySelect,
  collapsed,
  onCollapse,
  onExpand,
}: HomeLandingIntroProps) {
  const mobileRegions = regionPills.map((region) => ({
    ...region,
    href: getRegionPathFromName(region.name),
  }));
  const mobileCategories = categoryPills.map((category) => ({
    ...category,
    href: getCategoryPathFromName(category.name),
  }));

  return (
    <div data-testid="homepage-discovery-hero-wrapper">
      <h1 id="homepage-primary-heading-mobile" className="sr-only md:hidden">
        {heading}
      </h1>
      <MobileBrowseSheet
        heading={heading}
        open={!collapsed}
        resultCount={mobileResultCount}
        regions={mobileRegions}
        categories={mobileCategories}
        onOpenChange={(nextOpen) => (nextOpen ? onExpand() : onCollapse())}
        onRegionSelect={onMobileRegionSelect}
        onCategorySelect={onMobileCategorySelect}
      />
      <section
        aria-labelledby="homepage-primary-heading"
        hidden={collapsed}
        className={`hidden overflow-hidden transition-[max-height,opacity,margin] duration-300 ease-out md:block ${collapsed ? "mb-0 max-h-0 opacity-0 pointer-events-none" : "mb-4 md:mb-3 max-h-[1200px] opacity-100"}`}
        data-testid="homepage-seo-intro"
      >
        <div className="rounded-2xl border border-[#ece7da] bg-[linear-gradient(180deg,rgba(255,252,244,0.98),rgba(255,255,255,0.98))] px-3 py-3 shadow-[0_1px_6px_rgba(0,0,0,0.03)] md:px-4 md:py-3 lg:px-4 lg:py-2">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.16em] text-muted-foreground md:text-[11px] md:tracking-[0.18em]">
                Discovery
              </p>
              {!collapsed && (
                <h1
                  id="homepage-primary-heading"
                  className="mt-1 text-[1.5rem] font-semibold tracking-tight text-foreground md:text-lg lg:text-[1.18rem]"
                >
                  {heading}
                </h1>
              )}
            </div>
            <button
              type="button"
              onClick={onCollapse}
              className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-[14px] border border-[#d7e4d9] bg-[#f4f8f3] px-3 py-2 text-[13px] font-semibold text-[#335547] shadow-[0_2px_5px_rgba(0,0,0,0.06)] transition-[background-color,border-color,box-shadow,transform] duration-100 hover:border-[#bfd4c4] hover:bg-[#edf5ee] active:translate-y-[1px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2 md:min-h-[36px] md:px-3 md:py-1.5 md:text-[13px]"
              aria-expanded={!collapsed}
              data-testid="homepage-discovery-hero-collapse"
            >
              Hide browse
              <ChevronUp className="h-4 w-4" />
            </button>
          </div>

          <p className="mt-2 max-w-5xl text-[14px] leading-6 text-muted-foreground md:hidden">
            {MOBILE_INTRO}
          </p>
          <p className="mt-2 hidden max-w-none text-[13px] leading-[1.45] text-muted-foreground md:block lg:text-[0.86rem] lg:leading-[1.5]">
            {intro}
          </p>

          <p className="mt-2 text-[12px] leading-5 text-muted-foreground md:hidden">
            {MOBILE_FILTERS_HINT}
          </p>
          <p className="mt-1 hidden text-[10px] leading-4 text-muted-foreground md:block">
            Use the Filters button for fine-grained control - all 22 regions, 94+ sources, and advanced filtering.
          </p>

          <div className="mt-4 grid gap-3 md:mt-3 md:grid-cols-2 md:gap-3 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] lg:gap-3">
            <section
              aria-labelledby="homepage-browse-region-heading"
              className={discoverySectionCardClassName}
            >
              <h2
                id="homepage-browse-region-heading"
                className={discoverySectionHeadingClassName}
              >
                Browse by Region
              </h2>
              <div className="mt-3 flex flex-wrap gap-x-2 gap-y-2 overflow-visible whitespace-normal md:mt-2 md:gap-y-1.5">
                {showAllRegions && (
                  <Link
                    href="/"
                    onClick={(event) => {
                      event.preventDefault();
                      onAllRegionsClick?.();
                    }}
                    className={discoveryChipClassName}
                    style={discoveryChipIdleStyle}
                    data-testid="hero-region-pill-all-regions"
                  >
                    All regions
                  </Link>
                )}
                {regionPills.map((region) => (
                  <Link
                    key={region.name}
                    href={getRegionPathFromName(region.name)}
                    onClick={(event) => {
                      event.preventDefault();
                      onRegionClick(region.name);
                    }}
                    className={discoveryChipClassName}
                    style={region.active ? discoveryChipActiveStyle : discoveryChipIdleStyle}
                    data-testid={`hero-region-pill-${region.name.toLowerCase()}`}
                  >
                    {region.name}
                  </Link>
                ))}
              </div>
            </section>
            <section
              aria-labelledby="homepage-browse-category-heading"
              className={discoverySectionCardClassName}
            >
              <h2
                id="homepage-browse-category-heading"
                className={discoverySectionHeadingClassName}
              >
                Browse by Category
              </h2>
              <div className="mt-3 flex flex-wrap gap-x-2 gap-y-2 overflow-visible whitespace-normal md:mt-2 md:gap-y-1.5">
                {categoryPills.map((category) => (
                  <Link
                    key={category.name}
                    href={getCategoryPathFromName(category.name)}
                    onClick={(event) => {
                      event.preventDefault();
                      onCategoryClick(category.name);
                    }}
                    className={discoveryChipClassName}
                    style={category.active ? discoveryChipActiveStyle : discoveryChipIdleStyle}
                    data-testid={`hero-category-pill-${category.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </div>
      </section>

      <div
        className={`hidden overflow-hidden transition-[max-height,opacity,margin] duration-300 ease-out md:block ${collapsed ? "mb-2 max-h-24 opacity-100" : "mb-0 max-h-0 opacity-0 pointer-events-none"}`}
        data-testid="homepage-discovery-hero-collapsed-wrapper"
      >
        {collapsed && <h1 id="homepage-primary-heading" className="sr-only">{heading}</h1>}
        <button
          type="button"
          onClick={onExpand}
          className="flex min-h-[44px] w-full items-center gap-2 rounded-[14px] border border-[#d7e4d9] bg-[linear-gradient(180deg,rgba(244,248,243,0.98),rgba(255,255,255,0.98))] px-3 py-2 text-left transition-[background-color,border-color,box-shadow] hover:border-[#bfd4c4] hover:bg-[linear-gradient(180deg,rgba(237,245,238,0.98),rgba(255,255,255,0.98))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2 lg:px-4 lg:py-1"
          aria-expanded={!collapsed}
          data-testid="homepage-discovery-hero-collapsed"
        >
          <div className="flex min-w-0 items-center gap-1.5">
            <p className="min-w-0 text-[12px] font-semibold leading-snug text-foreground sm:text-[13px]">
              Find local events from 90+ calendars
            </p>
          </div>
          <span className="ml-auto inline-flex min-h-[32px] shrink-0 items-center gap-1 rounded-full border border-[#9FE1CB] bg-[#edf8f3] px-2.5 py-1 text-[11px] font-semibold leading-none text-[#1D9E75]">
            Browse
            <ChevronDown className="h-3 w-3" />
          </span>
        </button>
      </div>
    </div>
  );
}

export const HomeLandingIntro = memo(HomeLandingIntroInner);
